import Icon from '@components/common/Icon';
import { RESEARCH_DATA } from '@lib/constants';

const ResearchSection = () => {
  // Use centralized research data
  const researchPapers = RESEARCH_DATA.papers;
  const researchExperiences = RESEARCH_DATA.experiences;

  const handlePaperClick = (paper) => {
    if (paper.isPdf) {
      window.open(paper.file, '_blank');
    }
  };

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold"
            style={{ color: 'var(--color-heading)' }}
          >
            Research and Publications
          </h2>
          <p
            className="text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Exploring the intersection of artificial intelligence, autonomous vehicles, and chess strategy through academic research
          </p>
        </div>

        {/* Publications */}
        <div className="mb-12">
          <h3
            className="text-2xl sm:text-3xl font-heading font-bold mb-6"
            style={{ color: 'var(--color-heading)' }}
          >
            Publications
          </h3>
          <div className="space-y-6">
            {researchPapers.map((paper) => (
              <article
                key={paper.id}
                className="p-6 sm:p-8 rounded-xl border-2 transition-all duration-300 hover:shadow-card-hover cursor-pointer group"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                }}
                onClick={() => handlePaperClick(paper)}
              >
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <div
                    className="p-4 rounded-lg shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-1)' }}
                  >
                    <Icon
                      name="research"
                      size="xl"
                      style={{ color: 'var(--color-accent-7)' }}
                    />
                  </div>

                  <div className="flex-1 w-full">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-3">
                      <h4
                        className="text-xl sm:text-2xl font-bold group-hover:underline"
                        style={{ color: 'var(--color-heading)' }}
                      >
                        {paper.title}
                      </h4>
                      <span
                        className="px-3 py-1 rounded-full text-sm font-semibold shrink-0"
                        style={{
                          backgroundColor: 'var(--color-accent-1)',
                          color: 'var(--color-accent-7)',
                        }}
                      >
                        {paper.year}
                      </span>
                    </div>

                    <p
                      className="text-base sm:text-lg italic mb-4"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      {paper.fullTitle}
                    </p>

                    <p
                      className="mb-4 leading-relaxed text-sm sm:text-base"
                      style={{ color: 'var(--color-card-text)' }}
                    >
                      {paper.abstract}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {paper.topics.map((topic) => (
                        <span
                          key={topic}
                          className="px-3 py-1 rounded-full text-xs sm:text-sm"
                          style={{
                            backgroundColor: 'var(--color-theme-2)',
                            color: 'var(--color-subheading)',
                          }}
                        >
                          {topic}
                        </span>
                      ))}
                    </div>

                    <button
                      className="flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base transition-all duration-300 group-hover:scale-105"
                      style={{
                        backgroundColor: 'var(--color-accent-6)',
                        color: 'var(--color-button-primary-text)',
                      }}
                    >
                      <span>Download PDF</span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Research Experience */}
        <div>
          <h3
            className="text-2xl sm:text-3xl font-heading font-bold mb-6"
            style={{ color: 'var(--color-heading)' }}
          >
            Research Experience
          </h3>
          <div className="space-y-6">
            {researchExperiences.map((exp) => (
              <article
                key={exp.id}
                className="p-6 sm:p-8 rounded-xl border-2 transition-all duration-300 hover:shadow-card-hover"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                }}
              >
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <div
                    className="p-4 rounded-lg shrink-0"
                    style={{ backgroundColor: 'var(--color-accent-1)' }}
                  >
                    <Icon
                      name="research"
                      size="xl"
                      style={{ color: 'var(--color-accent-7)' }}
                    />
                  </div>

                  <div className="flex-1 w-full">
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-3">
                      <h4
                        className="text-xl sm:text-2xl font-bold"
                        style={{ color: 'var(--color-heading)' }}
                      >
                        {exp.title}
                      </h4>
                      <span
                        className="px-3 py-1 rounded-full text-sm font-semibold shrink-0"
                        style={{
                          backgroundColor: 'var(--color-accent-1)',
                          color: 'var(--color-accent-7)',
                        }}
                      >
                        {exp.year}
                      </span>
                    </div>

                    <p
                      className="text-base sm:text-lg font-semibold mb-2"
                      style={{ color: 'var(--color-subheading)' }}
                    >
                      {exp.institution}
                    </p>

                    {exp.supervisor && (
                      <p
                        className="text-sm mb-4"
                        style={{ color: 'var(--color-muted)' }}
                      >
                        Supervisor: {exp.supervisor}
                      </p>
                    )}

                    <p
                      className="mb-4 leading-relaxed text-sm sm:text-base"
                      style={{ color: 'var(--color-card-text)' }}
                    >
                      {exp.abstract}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {exp.topics.map((topic) => (
                        <span
                          key={topic}
                          className="px-3 py-1 rounded-full text-xs sm:text-sm"
                          style={{
                            backgroundColor: 'var(--color-theme-2)',
                            color: 'var(--color-subheading)',
                          }}
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResearchSection;
